import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import { CARE_PROFILE_SECTION_IDS } from "../../../utils/careProfileSections";
import colors from "../../../assets/theme/colors";
import { getPhoneHref } from "../../../utils/contactPhone";

export const SECTION_ICON_BY_ID = {
  [CARE_PROFILE_SECTION_IDS.ABOUT]: DescriptionOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.MEDICATION]: MedicationOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD]: RestaurantMenuOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.CARE_TEAM]: GroupsOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT]: FavoriteBorderRoundedIcon,
  [CARE_PROFILE_SECTION_IDS.GOALS]: FlagOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT]: PsychologyAltOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE]: AutoAwesomeRoundedIcon,
  [CARE_PROFILE_SECTION_IDS.ROUTINE_TRANSITIONS]: EventRepeatOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.COMMUNICATION]: ChatBubbleOutlineRoundedIcon,
  [CARE_PROFILE_SECTION_IDS.MEMORY_ORIENTATION]: MonitorHeartOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.MOBILITY_SAFETY]: AccessibilityNewOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.SLEEP]: BedtimeOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.SYMPTOMS_MONITORING]: MonitorHeartOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL]: HealthAndSafetyOutlinedIcon,
};

const storyTones = colors.semantic.profileFlow.careStory.tones;

const TONE_STYLES = {
  about: [storyTones.who.markerBg, storyTones.who.headerInk],
  medical: [storyTones.health.markerBg, storyTones.health.headerInk],
  allergy: [storyTones.health.chipBg, storyTones.health.headerInk],
  support: [storyTones.whatHelps.markerBg, storyTones.whatHelps.headerInk],
  team: [storyTones.emergency.markerBg, storyTones.emergency.headerInk],
  goals: [storyTones.rhythm.markerBg, storyTones.rhythm.headerInk],
  behavior: [storyTones.hardMoments.markerBg, storyTones.hardMoments.headerInk],
  sensory: [storyTones.sensory.markerBg, storyTones.sensory.headerInk],
  routine: [storyTones.rhythm.markerBg, storyTones.rhythm.headerInk],
  communication: [storyTones.whatHelps.markerBg, storyTones.whatHelps.headerInk],
  memory: [storyTones.rhythm.markerBg, storyTones.rhythm.headerInk],
  safety: [storyTones.hardMoments.markerBg, storyTones.hardMoments.headerInk],
  sleep: [storyTones.rhythm.markerBg, storyTones.rhythm.headerInk],
  emergency: [storyTones.emergency.markerBg, storyTones.emergency.headerInk],
};

export const getToneStyle = (tone) => {
  const [bg, ink] = TONE_STYLES[tone] || TONE_STYLES.support;
  return { bg, ink };
};

const getNeedToKnowIcon = (tone) => {
  if (tone === "medical") return MedicationOutlinedIcon;
  if (tone === "support") return FavoriteBorderRoundedIcon;
  return WarningAmberRoundedIcon;
};

export const NeedToKnowCard = ({ items = [] }) => {
  if (!items.length) return null;

  return (
    <Box
      sx={{
        borderRadius: "24px",
        border: `2px solid ${alpha(colors.mood.happy, 0.42)}`,
        bgcolor: alpha(colors.mood.happy, 0.08),
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 1, display: "flex", alignItems: "center", gap: 0.85, borderBottom: `1px solid ${alpha(colors.mood.happy, 0.35)}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: colors.semantic.profileFlow.healthIconInk }} />
          <Typography sx={{ fontSize: { xs: "0.98rem", sm: "1.04rem" }, fontWeight: 500, color: colors.semantic.profileFlow.healthIconInk }}>
            Need to know
          </Typography>
        </Box>
      </Box>
      <Stack spacing={0.95} sx={{ px: { xs: 1.25, sm: 1.5 }, py: 1.25 }}>
        {items.map((item) => {
          const tone = getToneStyle(item.tone);
          const ItemIcon = getNeedToKnowIcon(item.tone);
          const phoneHref = item.id === "emergency_contact" ? getPhoneHref(item.text) : "";

          return (
            <Box
              key={item.id}
              sx={{
                display: "grid",
                gridTemplateColumns: phoneHref ? "auto minmax(0, 1fr) auto" : "auto minmax(0, 1fr)",
                gap: 1.05,
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "13px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(tone.bg, 0.72),
                  color: tone.ink,
                  flexShrink: 0,
                }}
              >
                <ItemIcon sx={{ fontSize: 19 }} />
              </Box>
              <Typography sx={{ fontSize: { xs: "0.92rem", sm: "0.98rem" }, lineHeight: 1.45, color: colors.semantic.ink }}>
                <Box component="span" sx={{ fontWeight: 500, color: tone.ink }}>
                  {item.label}
                </Box>
                {" · "}
                {item.text}
              </Typography>
              {phoneHref ? (
                <IconButton
                  component="a"
                  href={phoneHref}
                  aria-label={`Call ${item.label}`}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "13px",
                    bgcolor: colors.semantic.profileFlow.doneBg,
                    color: colors.semantic.profileFlow.doneText,
                    "&:hover": { bgcolor: alpha(colors.semantic.profileFlow.doneBg, 0.78) },
                  }}
                >
                  <CallOutlinedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export const CareProfileSectionCard = ({ section, onSectionSelect, onStepChange, onOpenCareTeam }) => {
  const SectionIcon = SECTION_ICON_BY_ID[section.id] || DescriptionOutlinedIcon;
  const tone = getToneStyle(section.tone);
  const canOpen = Boolean(section.targetStep || (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM && onOpenCareTeam));
  const handleClick = () => {
    if (section.targetStep) {
      if (onSectionSelect) {
        onSectionSelect(section);
        return;
      }
      onStepChange?.(section.targetStep);
    } else if (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM) {
      onOpenCareTeam?.();
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={!canOpen}
      variant="text"
      sx={{
        width: "100%",
        textTransform: "none",
        p: 0,
        borderRadius: "22px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        overflow: "hidden",
        opacity: 1,
        boxShadow: `0 4px 12px ${alpha(colors.semantic.ink, 0.03)}`,
        "&.Mui-disabled": { opacity: 1, color: "inherit" },
        "&:hover": { bgcolor: colors.semantic.profileFlow.cardBg, borderColor: alpha(colors.semantic.profileFlow.completionText, 0.34) },
      }}
    >
      <Box sx={{ width: "100%", px: { xs: 1.35, sm: 1.6 }, py: { xs: 1.2, sm: 1.35 }, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1.15, alignItems: "center" }}>
        <Box
          sx={{
            width: 58,
            height: 58,
            borderRadius: "18px",
            bgcolor: tone.bg,
            color: tone.ink,
            display: "grid",
            placeItems: "center",
          }}
        >
          <SectionIcon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0, textAlign: "left" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: { xs: "1.08rem", sm: "1.18rem" }, fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.1 }}>
              {section.label}
            </Typography>
            <Chip
              label={section.statusLabel}
              size="small"
              sx={{
                height: 24,
                borderRadius: 999,
                fontSize: "0.72rem",
                fontWeight: 500,
                bgcolor: section.hasDetails ? colors.semantic.profileFlow.doneBg : colors.semantic.profileFlow.notStartedBg,
                color: section.hasDetails ? colors.semantic.profileFlow.doneText : colors.semantic.profileFlow.notStartedText,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
          <Typography noWrap sx={{ mt: 0.4, fontSize: { xs: "0.92rem", sm: "1.02rem" }, color: colors.semantic.ink2, lineHeight: 1.25 }}>
            {section.description}
          </Typography>
        </Box>

        {canOpen ? (
          <Typography sx={{ fontSize: { xs: "0.98rem", sm: "1.08rem" }, fontWeight: 500, color: section.hasDetails ? colors.semantic.profileFlow.completionText : colors.semantic.profileFlow.notStartedText, whiteSpace: "nowrap" }}>
            {section.actionLabel}
          </Typography>
        ) : null}
      </Box>
    </Button>
  );
};
